import type { SuggestionItem } from "@digdir/designsystemet-react";
import { z } from "zod";
import { formatNumberWithSpaces } from "@/utils";
import type { BestillingContextDataType } from "../context";
import { type Aktoer, useAktoerData } from "../hooks";

const aktoerOrgNrSchema = z
  .number()
  .int()
  .positive()
  .gte(100000000)
  .lte(999999999);
const aktoerOrgNrString = z.string();
const aktoerSchema = z
  .object({
    type: z.enum(["Privat", "Selskap"]),
    organisasjonsnummer: aktoerOrgNrString.optional(),
  })
  .refine(
    (data) =>
      data.type === "Privat" ||
      Number.isInteger(parseOrgNr(data.organisasjonsnummer)),
    {
      message: "pages.aktoer.form.aktoer.orgnr-ugyldig",
    },
  );

export type AktoerSelected = SuggestionItem;

export const parseOrgNr = (orgNr: string | undefined): number | undefined => {
  if (orgNr && aktoerOrgNrSchema.safeParse(Number(orgNr)).success) {
    return Number(orgNr);
  }
  return undefined;
};

export const aktoerFormSchema = z.object({
  aktoer: aktoerSchema
    .optional()
    .refine((value) => value !== null && value !== undefined, {
      message: "pages.aktoer.form.aktoer.paaakrevd",
    }),
});

export type aktoerFormValues = z.infer<typeof aktoerFormSchema>;

export const useFromToSelected = (
  aktoer: aktoerFormValues["aktoer"],
  mySelfSelect: AktoerSelected,
): string | AktoerSelected => {
  const { getAktoerByOrgNr } = useAktoerData();
  if (!aktoer) return "";
  switch (aktoer.type) {
    case "Privat":
      return mySelfSelect;
    case "Selskap": {
      if (!aktoer.organisasjonsnummer) return "";
      const selskap = getAktoerByOrgNr(aktoer.organisasjonsnummer);
      if (!selskap) {
        return "";
      }
      return {
        label: formatAktoerNavn(selskap),
        value: selskap.organisasjonsnummer.toString(),
      };
    }
  }
};

export const formatAktoerNavn = (aktoer: Aktoer) => {
  return `${aktoer.navn} (${formatNumberWithSpaces(aktoer.organisasjonsnummer)})`;
};

export const convertSelctedToForm = (
  item: AktoerSelected | undefined,
  mySelfSelect: AktoerSelected,
): aktoerFormValues["aktoer"] | undefined => {
  if (!item) return undefined;
  if (item.value === mySelfSelect.value) {
    return { type: "Privat" as const };
  }
  return {
    type: "Selskap" as const,
    organisasjonsnummer: item.value,
  };
};

export function getDefaultAktoerFormValues(
  bestilling: BestillingContextDataType,
): aktoerFormValues | undefined {
  return {
    aktoer:
      (bestilling.aktoer && {
        type: bestilling.aktoer.type,
        organisasjonsnummer:
          bestilling.aktoer.type === "Selskap"
            ? `${bestilling.aktoer.organisasjonsnummer}`
            : undefined,
      }) ||
      undefined,
  };
}
